import React, { useEffect, useState, useCallback } from 'react';
import API_URL from '@/config/api';

/**
 * useNotificationCounts — polls /api/notifications/counts and exposes the latest counts.
 * Returns: { total, bySection: {probound, proepoxy, prodecor}, refresh }
 *
 * Polls every 30 seconds while the tab is visible. Pauses while the tab is hidden.
 */
export default function useNotificationCounts(pollMs = 30000) {
  const [data, setData] = useState({
    total: 0,
    bySection: { probound: 0, proepoxy: 0, prodecor: 0 },
    bySectionCategory: {
      probound: { lead: 0, order: 0, offer: 0 },
      proepoxy: { lead: 0, order: 0, offer: 0 },
      prodecor: { lead: 0, order: 0, offer: 0 },
    },
  });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/counts`, { credentials: 'include' });
      if (!res.ok) return;
      const json = await res.json();
      setData({
        total: json.total || 0,
        bySection: {
          probound: json.by_section?.probound || 0,
          proepoxy: json.by_section?.proepoxy || 0,
          prodecor: json.by_section?.prodecor || 0,
        },
        bySectionCategory: json.by_section_category || {
          probound: { lead: 0, order: 0, offer: 0 },
          proepoxy: { lead: 0, order: 0, offer: 0 },
          prodecor: { lead: 0, order: 0, offer: 0 },
        },
      });
    } catch (_) {
      /* silent — user might be logged out */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, pollMs);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh, pollMs]);

  return { ...data, refresh };
}
